// import MainReport from '../features/report/components/MainReport';
import {createMuiTheme, MuiThemeProvider} from '@material-ui/core/styles';
import store from 'app/store';
import AppReloader from 'components/Reloader/AppReloader';
import 'devextreme/dist/css/dx.common.css';
import 'devextreme/dist/css/dx.material.blue.light.compact.css';
import I18nProvider from 'features/i18n/I18nProvider';
import navigationRoutes from 'features/navigation/navigationRoutes';
import {AppRoute, LoginRoute} from 'features/navigation/Routes';
import React from 'react';
import {Provider, useSelector} from 'react-redux';
import {BrowserRouter, Redirect, Route, Switch} from 'react-router-dom';
import {Dashboard, Login, RecoverPassword, ResetPassword} from '../features/';
import useCacheBuster, {
  refreshCacheAndReload,
} from '../helpers/cache/useCacheBuster';
import {
  configureActivityTracking,
  stopActivityTracking,
} from '../helpers/session';
import '../helpers/axiosGuard';

const theme = createMuiTheme({
  palette: {
    primary: {
      main: '#1C4257',
    },
    secondary: {
      main: '#56D500',
    },
  },
});

const displayRoutes = (routes, role) => {
  const appRoutes = [];
  const key = {ix: 0};
  extractAppRoutes(routes, appRoutes, key, role);
  return appRoutes;
};

const extractAppRoutes = (routes, pusheable, key, role) => {
  routes.forEach((route) => {
    if (route.group) {
      extractAppRoutes(route.items, pusheable, key, role);
    } else if (route.role && route.role.includes(role)) {
      pusheable.push(
        <AppRoute
          key={key.ix++}
          exact
          path={`/${route.path}`}
          component={route.component}
        />,
      );
    }
  });
};

const Routes = (props) => {
  const role = useSelector(
    (state) => state?.authentication?.user?.roles?.[0]?.role,
  );
  return (
    <Switch>
      <LoginRoute exact path="/login" component={Login} />
      <Route exact path="/recover-password" component={RecoverPassword} />
      <Route exact path="/reset-password" component={ResetPassword} />
      <AppRoute
        exact
        path="/change-password"
        component={require('features/login/ChangePassword').default}
      />
      <AppRoute exact path="/" component={Dashboard} />
      {displayRoutes(navigationRoutes, role)}
      <Route render={() => <Redirect to="/" />} />
    </Switch>
  );
};

const SessionBootstrap = () => {
  const user = useSelector((state) => state?.authentication?.user);

  React.useEffect(() => {
    configureActivityTracking(user);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    user?.token,
    user?.Token,
    user?.idleTimeoutMinutes,
    user?.IdleTimeoutMinutes,
    user?.cwid,
    user?.Cwid,
    user?.name,
    user?.Name,
  ]);

  React.useEffect(() => {
    return () => {
      stopActivityTracking();
    };
  }, []);

  return null;
};

const handleForceRetry = async () => {
  try {
    sessionStorage.removeItem('cache_bust_reload_count');
    sessionStorage.removeItem('cache_bust_reload_version');
  } catch {
    // ignorar
  }

  if ('serviceWorker' in navigator) {
    const regs = await navigator.serviceWorker.getRegistrations();
    await Promise.all(regs.map((r) => r.unregister()));
  }
  if (typeof caches !== 'undefined') {
    const names = await caches.keys();
    await Promise.all(names.map((n) => caches.delete(n)));
  }

  // Navegar a raíz limpia sin _cbv en la URL
  window.location.replace(`/?_retry=${Date.now()}`);
};

const CacheRecoveryScreen = () => (
  <div
    style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      height: '100vh',
      fontFamily: 'sans-serif',
      padding: '2rem',
      textAlign: 'center',
    }}>
    <h2>Nueva versión disponible</h2>
    <p>
      No fue posible actualizar automáticamente. Intentá recargar manualmente.
    </p>
    <button
      onClick={handleForceRetry}
      style={{
        marginTop: '1rem',
        padding: '0.75rem 2rem',
        fontSize: '1rem',
        cursor: 'pointer',
        backgroundColor: '#1C4257',
        color: '#fff',
        border: 'none',
        borderRadius: '4px',
      }}>
      Reintentar actualización
    </button>
    <p style={{fontSize: '0.85rem', color: '#666', marginTop: '1.5rem'}}>
      Si el problema persiste luego de varios intentos, contacte al soporte
      técnico.
    </p>
  </div>
);

const App = () => {
  const {loading, isLatestVersion, needsManualRefresh} = useCacheBuster();
  if (loading) {
    return null;
  }
  if (needsManualRefresh) {
    return <CacheRecoveryScreen />;
  }
  return (
    <Provider store={store}>
      <SessionBootstrap />
      <AppReloader>
        <BrowserRouter>
          <MuiThemeProvider theme={theme}>
            <I18nProvider>
              <Routes />
            </I18nProvider>
          </MuiThemeProvider>
        </BrowserRouter>
      </AppReloader>
    </Provider>
  );
};

export default App;
